import {
  Button,
  Col,
  Form,
  Row,
  Select,
  Spin,
  Switch,
  notification,
} from 'antd';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import Table from './../../components/Table';
import { getData } from '../../store/reducers/orderSlice';
import {
  setQuery,
  setSelect,
  setUrl,
} from '../../store/reducers/dataSlice';
import { updateIsFulfilled, updateStatus } from '../../store/thunks/order';
import moment from 'moment';
import {
  ADMIN_ROLE_ID,
  DISTRICT_ROLE_ID,
} from '../../services/constants/roles';
import { setSelectedMenu } from '../../store/reducers/uiSlice';
import TextArea from 'antd/lib/input/TextArea';
import Quantity from '../../components/Quantity';
import { useCallback } from 'react';
import _ from 'lodash';
import {
  ORDER_QUANTITY_MIN_ERROR,
  parseOrderLineQuantity,
} from '../../services/orderQuantity';
import DeleteModal from '../../components/DeleteModal';
import {
  DELIVERY_FREEZE_ERROR,
  isDeliveryDateFrozen,
  orderIsDeliveryFrozen,
} from '../../services/orderDeliveryFreeze';

function OrderInfoPage(props) {
  const { Option } = Select;
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isFormReady, setIsFormReady] = useState(false);
  const [permit, setPermit] = useState(true);
  const [visibleCancellationReason, setVisibleCancellationReason] =
    useState(false);
  const [fulfilledCommodity, setFulfilledCommodity] = useState(0);
  const [fulfilledRegular, setFulfilledRegular] = useState(0);
  const [dataSource, setDataSource] = useState([]);
  const [locallyRemovedLineIds, setLocallyRemovedLineIds] = useState([]);
  const [canCancel, setCanCancel] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const isFrozen = orderIsDeliveryFrozen(props.data);

  useEffect(() => {
    setLocallyRemovedLineIds([]);
    setDataSource([]);
  }, [id]);

  useEffect(() => {
    setPermit(!!props.data?.created_at);
    props.setSelectedMenu('2');
    dispatch(getData(id));
    calculateFullFilledSum(id);
    if (props.roleId === ADMIN_ROLE_ID) {
      setCanCancel(props.data?.OrderStatus !== 'cancelled');
      setCanEdit(!isFrozen);
    } else {
      setCanEdit(
        !isFrozen &&
          props.data.OrderStatus === 'new' &&
          props.data?.passedInMinutes < 24 * 60,
      );
      setCanCancel(
        props.data?.OrderStatus !== 'cancelled' &&
          props.data?.passedInMinutes < 24 * 60,
      );
    }
  }, [
    props.roleId,
    props.data.created_at,
    props.data.DeliveryDate,
    props.data.isDeliveryFrozen,
    props.data.OrderStatus,
    props.data?.passedInMinutes,
    isFrozen,
    dispatch,
    id,
  ]);

  // Check if user is Admin
  const isAdmin = props.myData?.role?.name === 'Admin';
  const canMutateLines = isAdmin && !isFrozen;

  useEffect(() => {
    if (props.tableData && props.tableData.length > 0) {
      setDataSource((prevDataSource) => {
        // Keep local quantity edits and omit lines removed only in the UI.
        return props.tableData
          .filter((item) => !locallyRemovedLineIds.includes(item.id))
          .map((newItem) => {
            const existingItem = prevDataSource.find(
              (item) => item.id === newItem.id,
            );
            if (existingItem && existingItem.Quantity !== newItem.Quantity) {
              return {
                ...newItem,
                Quantity: existingItem.Quantity,
              };
            }
            return newItem;
          });
      });
    }
  }, [props.tableData, locallyRemovedLineIds]);

  const calculateFullFilledSum = (id) => {
    axios
      .get(`/custom/GetQ?command=select * from "getFullFilledSum"(${id})`)
      .then((res) => {
        const { results } = res.data;
        setFulfilledRegular(results?.[0]?.FulfilledRegular);
        setFulfilledCommodity(results?.[0]?.FulfilledCommodity);
      });
  };

  useLayoutEffect(() => {
    props.setUrl('/orders-products');
    props.setSelect(`&OrderId.id=${id}&`);
    // Sort/limit query is owned by Table (localStorage + defSortKey) so the
    // user's column sort survives navigating to a product and back.
  }, [id]);

  const OnFulfilledChange = (value, rec) => {
    new Promise((resolve) => {
      dispatch(
        updateIsFulfilled({
          id: rec.id,
          IsFulfilled: value,
          FulfilledQuantity: value === false ? 0 : rec?.FulfilledQuantity,
        }),
      );

      resolve();
    }).then(() => {
      setDataSource(
        dataSource.map((obj) =>
          obj.id === rec.id
            ? {
                ...obj,
                IsFulfilled: value,
                FulfilledQuantity: value === false ? 0 : rec?.FulfilledQuantity,
              }
            : obj,
        ),
      );
      setTimeout(() => calculateFullFilledSum(id), 1000);
    });
  };

  const debouncedChangeHandler = useCallback(
    _.debounce((req) => {
      new Promise((resolve) => {
        dispatch(
          updateIsFulfilled({ id: req?.id, FulfilledQuantity: req?.value }),
        );
        resolve();
      }).then(() => {
        setTimeout(() => calculateFullFilledSum(id), 1000);
      });
    }, 500),
    [],
  );

  // Local-only quantity updates on detail. Persist only via Edit wizard.
  const handleLocalQuantityChange = useCallback(
    (req) => {
      if (orderIsDeliveryFrozen(props.data)) {
        notification.error({
          message: 'Error',
          description: DELIVERY_FREEZE_ERROR,
          placement: 'bottomRight',
        });
        return;
      }

      const quantity = parseOrderLineQuantity(req?.value);
      if (quantity === null || quantity < 0) {
        notification.error({
          message: 'Error',
          description: ORDER_QUANTITY_MIN_ERROR,
          placement: 'bottomRight',
        });
        return;
      }

      if (quantity === 0) {
        setLocallyRemovedLineIds((prev) =>
          prev.includes(req.id) ? prev : [...prev, req.id],
        );
        setDataSource((prevDataSource) =>
          prevDataSource.filter((item) => item.id !== req?.id),
        );
        return;
      }

      setDataSource((prevDataSource) => {
        const originalItem = prevDataSource.find((obj) => obj.id === req?.id);
        if (!originalItem || originalItem.Quantity === quantity) {
          return prevDataSource;
        }

        return prevDataSource.map((obj) =>
          obj.id === req?.id ? { ...obj, Quantity: quantity } : obj,
        );
      });
    },
    [props.data],
  );

  const columns = [
    {
      title: 'Code',
      dataIndex: 'ProductId',
      key: 'Code',
      render: (text, rec) =>
        text && text.Code && text.id ? (
          <a
            target="_blank"
            href={`/products/product/${text.id}`}
            style={{ color: 'black' }}
            rel="noreferrer"
          >
            {text.Code}
          </a>
        ) : (
          'N/A'
        ),
      sorter: {},
      searchable: true,
      location: 'ProductId.Code',
      width: 100,
    },
    {
      title: 'Product Name',
      dataIndex: 'ProductId',
      key: 'ProductId',
      // width: 200,
      ellipsis: {
        tooltip: true,
      },
      render: (text) => (text && text.Name ? text.Name : 'N/A'),
      sorter: {},
      searchable: true,
      location: 'ProductId.Name',
    },
    {
      title: 'Date Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (text) =>
        text ? (
          <div style={{ textAlign: 'center' }}>
            {moment(text).format('MM-DD-YYYY')}
          </div>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Quantity',
      dataIndex: 'Quantity',
      key: 'Quantity',
      render: (text, product) =>
        isAdmin ? (
          <Quantity
            text={text}
            min={1}
            max={999999}
            disabled={!canMutateLines}
            change={(value) =>
              handleLocalQuantityChange({ id: product?.id, value })
            }
            onRemove={() =>
              handleLocalQuantityChange({ id: product?.id, value: 0 })
            }
          />
        ) : text ? (
          <div style={{ textAlign: 'center' }}>{text}</div>
        ) : (
          'N/A'
        ),
      width: 140,
    },
    {
      title: 'Time Lead',
      dataIndex: 'ProductId',
      key: 'TimeLead',
      render: (text) =>
        text &&
        (text.TimeLead ? (
          <div style={{ textAlign: 'center' }}>{text.TimeLead} days</div>
        ) : (
          'N/A'
        )),
      width: 100,
    },
    {
      title: 'Commercial Cost',
      dataIndex: 'ProductId',
      key: 'RegularPrice',
      render: (text) =>
        text && text.ReqularPrice ? (
          <div style={{ textAlign: 'right' }}>{`$ ${
            text.ReqularPrice && text.ReqularPrice.toFixed(2)
          }`}</div>
        ) : (
          'N/A'
        ),
      sorter: {},
      location: 'ProductId.ReqularPrice',
      width: 160,
    },
    {
      title: 'Commodity Price',
      dataIndex: 'ProductId',
      key: 'CommodityPrice',
      render: (text) =>
        text && text.CommidityPrice ? (
          <div style={{ textAlign: 'right' }}>{`$${
            text.CommidityPrice && text.CommidityPrice.toFixed(2)
          }`}</div>
        ) : (
          'N/A'
        ),
      sorter: {},
      width: 160,
      location: 'ProductId.CommidityPrice',
    },
    {
      title: 'Fulfilled',
      dataIndex: 'IsFulfilled',
      key: 'IsFulfilled',
      width: 100,
      render: (value, rec) => (
        <Switch
          onChange={(value) => OnFulfilledChange(value, rec)}
          checkedChildren="yes"
          unCheckedChildren="no"
          defaultChecked={value}
          disabled={visibleCancellationReason || !isAdmin}
        />
      ),
      location: 'ProductId.IsFulfilled',
    },
    {
      title: 'Fulfilled Quantity',
      dataIndex: 'FulfilledQuantity',
      key: 'FulfilledQuantity',
      render: (text, product) =>
        isAdmin ? (
          <Quantity
            text={text}
            min={0}
            max={product?.Quantity}
            disabled={!product?.IsFulfilled}
            change={(value) =>
              debouncedChangeHandler({ id: product?.id, value })
            }
          />
        ) : (
          text || 0
        ),
      width: 140,
    },
    {
      title: 'Commercial Total',
      dataIndex: 'ProductId',
      key: 'ProductId',
      render: (text, rec) => {
        // Find the updated quantity from dataSource
        const updatedRecord = dataSource.find((item) => item.id === rec.id);
        const currentQuantity = updatedRecord
          ? updatedRecord.Quantity
          : rec.Quantity;

        return (
          <>
            {text?.ReqularPrice ? (
              <div style={{ textAlign: 'right' }}>{`$${(
                text.ReqularPrice * currentQuantity
              ).toFixed(2)}`}</div>
            ) : (
              'N/A'
            )}
          </>
        );
      },
      sorter: {},
      width: 160,
    },
    {
      title: 'Commodity Total',
      dataIndex: 'Commodity',
      key: 'Commodity',
      render: (text, rec) => {
        // Find the updated quantity from dataSource
        const updatedRecord = dataSource.find((item) => item.id === rec.id);
        const currentQuantity = updatedRecord
          ? updatedRecord.Quantity
          : rec.Quantity;

        return (
          <div style={{ textAlign: 'right' }}>
            {rec?.ProductId?.CommidityPrice
              ? `$${Number.parseFloat(
                  Number(rec?.ProductId.CommidityPrice || 0) * currentQuantity,
                ).toFixed(2)}`
              : 'N/A'}
          </div>
        );
      },
      sorter: {},
      width: 160,
    },
  ];

  useEffect(() => {
    form.setFieldsValue({
      status: props.data.OrderStatus,
      cancellationReason: props.data.cancellationReason,
    });
    setVisibleCancellationReason(form.getFieldValue('status') === 'cancelled');
  }, [props.data]);

  const onFinish = (values) => {
    let data = {
      ...values,
      DeliveryDate: moment(values.DeliveryDate, 'MM-DD-YYYY').toDate,
      cancellationReason: values.cancellationReason,
      id,
    };
    dispatch(updateStatus(data));
  };
  const onCancel = (values) => {
    const payload = {
      id: props.data.id,
      status: 'cancelled',
      cancellationReason: `Cancelled By ${
        props.roleId === ADMIN_ROLE_ID
          ? 'Admin'
          : props.roleId === DISTRICT_ROLE_ID
            ? 'District'
            : 'School'
      }`,
    };
    if (props.data?.passedInMinutes < 24 * 60) {
      dispatch(updateStatus(payload));
    } else {
      notification.error({
        message: 'Changes to orders are only allowed within 24 hours',
        placement: 'bottomRight',
      });
    }
  };
  const onFinishFailed = () => {};

  // Calculate updated total commercial cost based on local quantity changes
  const calculateUpdatedTotalCommercialCost = () => {
    if (!dataSource || dataSource.length === 0) {
      return props.data.TotalRegularPrice
        ? props.data.TotalRegularPrice.toFixed(2)
        : '0.00';
    }

    const total = dataSource.reduce((sum, item) => {
      const regularPrice = item.ProductId?.ReqularPrice || 0;
      const quantity = item.Quantity || 0;
      return sum + regularPrice * quantity;
    }, 0);

    return total.toFixed(2);
  };

  const calculateUpdatedTotalCommodityCost = () => {
    if (!dataSource || dataSource.length === 0) {
      return props.data.TotalCommodityPrice
        ? props.data.TotalCommodityPrice.toFixed(2)
        : '0.00';
    }

    const total = dataSource.reduce((sum, item) => {
      const commodityPrice = item.ProductId?.CommidityPrice || 0;
      const quantity = item.Quantity || 0;
      return sum + commodityPrice * quantity;
    }, 0);

    return total.toFixed(2);
  };

  const formatCurrency = (value) => {
    const amount = Number(value);
    return value !== null &&
      value !== undefined &&
      value !== '' &&
      Number.isFinite(amount)
      ? `$${amount.toFixed(2)}`
      : 'N/A';
  };

  const formatText = (value) =>
    value !== null && value !== undefined && value !== '' ? value : 'N/A';

  const formatDate = (value) =>
    value ? moment(value).format('MM-DD-YYYY') : 'N/A';

  const getCurrentQuantity = (record) => {
    const updatedRecord = dataSource.find((item) => item.id === record.id);
    return updatedRecord ? updatedRecord.Quantity : record.Quantity;
  };

  const getCommercialTotal = (record) => {
    const price = Number(record.ProductId?.ReqularPrice);
    if (!Number.isFinite(price)) return 'N/A';
    return formatCurrency(price * Number(getCurrentQuantity(record) || 0));
  };

  const getCommodityTotal = (record) => {
    const price = Number(record.ProductId?.CommidityPrice);
    if (!Number.isFinite(price)) return 'N/A';
    return formatCurrency(price * Number(getCurrentQuantity(record) || 0));
  };

  const EveryInfo = ({ title, text }) => (
    <div className="every-info">
      <div className="title">{title}</div>
      <div className="text">{text}</div>
    </div>
  );

  const PrintInfo = ({ title, text }) => (
    <div className="print-info">
      <div className="print-info-title">{title}</div>
      <div className="print-info-text">{text}</div>
    </div>
  );

  const PrintProductInfo = ({ title, text }) => (
    <div className="print-product-info">
      <span className="print-product-title">{title}: </span>
      <span className="print-product-text">{text}</span>
    </div>
  );

  return (
    <>
      <div className="screen-only">
        <DeleteModal
          show={modalShow}
          setShow={setModalShow}
          deleteFunc={onCancel}
          mainMessage="Are you sure you want to cancel this order?"
          message=" "
        />
        <div className="order-info-page info-page">
        {permit && (
          <div className="container">
            <h2
              className="title  go-back remove-onPrint"
              onClick={() => navigate(-1)}
            >
              <img src="/vectors/arrow-left.png" alt="" />
              Go Back
            </h2>
            <h2 className="title"> Placed Order</h2>
            <Spin
              spinning={props.loading && props.tableLoading}
              tip="Loading..."
            >
              <Row>
                <Col md={13} span={24}>
                  <Row>
                    <Col span={8}>
                      <EveryInfo title="Order Number" text={props.data.id} />
                      <EveryInfo
                        title="District Name"
                        text={
                          props.data.DistrictUserId &&
                          props.data.DistrictUserId.DistrictOrSchoolName
                        }
                      />
                      <EveryInfo
                        title="Shipping Site Name"
                        text={
                          props.data.shippingSiteName
                            ? props.data.shippingSiteName
                            : 'N/A'
                        }
                      />
                      <EveryInfo
                        title="Total Commercial Cost"
                        text={`$${calculateUpdatedTotalCommercialCost()}`}
                      />

                      <EveryInfo
                        title="Total Fulfilled Commercial"
                        text={formatCurrency(fulfilledRegular)}
                      />
                    </Col>

                    <Col span={8}>
                      {props.data.SchoolUserId && (
                        <EveryInfo
                          title="School Name"
                          text={
                            props.data.SchoolUserId?.DistrictOrSchoolName
                              ? props.data.SchoolUserId.DistrictOrSchoolName
                              : 'N/A'
                          }
                        />
                      )}
                      <EveryInfo
                        title="Shipping Address"
                        text={
                          props?.myData?.shippingAddress ||
                          props?.data?.shippingAddress ||
                          'N/A'
                        }
                      />

                      <EveryInfo
                        title="Total Commodity Price"
                        text={`$${calculateUpdatedTotalCommodityCost()}`}
                      />
                      <EveryInfo
                        title="Total Fulfilled Commodity"
                        text={formatCurrency(fulfilledCommodity)}
                      />
                      <EveryInfo
                        title="Delivery Date"
                        text={moment(
                          props.data.DeliveryDate,
                          'YYYY-MM-DD',
                        ).format('MM-DD-YYYY')}
                      />
                    </Col>

                    <Col span={8}>
                      {props.data.OrderStatus === 'fullFilled' && (
                        <EveryInfo
                          title="Updated Date"
                          text={`${moment(
                            new Date(props.data.updated_at),
                          ).format(
                            'MM-DD-YYYY',
                          )}  ${props.data.updated_at.slice(11, 16)}`}
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
                <Col md={11} span={24}>
                  <Col style={{ textAlign: 'end' }} span={24}>
                    <Button
                      className="btn remove-onPrint"
                      type="primary"
                      onClick={() => window.print()}
                    >
                      Print
                    </Button>
                  </Col>
                  {props.roleId !== ADMIN_ROLE_ID && canCancel && (
                    <Form
                      className="order-info"
                      layout="vertical"
                      form={form}
                      name="basic"
                      labelCol={{
                        span: 8,
                      }}
                      wrapperCol={{
                        span: 24,
                      }}
                      initialValues={{
                        remember: true,
                        size: 'large',
                        status: props.data.OrderStatus,
                      }}
                      onFinish={() => setModalShow(true)}
                      autoComplete="off"
                    >
                      <Form.Item
                        wrapperCol={{
                          offset: 0,
                          span: 24,
                        }}
                        style={{ textAlign: 'right' }}
                      >
                        <Button
                          className="btn"
                          type="primary"
                          htmlType="submit"
                          disabled={props.data?.passedInMinutes >= 24 * 60}
                        >
                          Cancel Order
                        </Button>
                      </Form.Item>
                    </Form>
                  )}
                  {props.roleId === ADMIN_ROLE_ID ? (
                    props.data.OrderStatus ? (
                      <Form
                        className="order-info"
                        layout="vertical"
                        form={form}
                        name="basic"
                        labelCol={{
                          span: 8,
                        }}
                        wrapperCol={{
                          span: 24,
                        }}
                        initialValues={{
                          remember: true,
                          size: 'large',
                          status: props.data.OrderStatus,
                        }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        autoComplete="off"
                      >
                        <Row>
                          <Col span={24}>
                            <div className="card">
                              <Form.Item
                                label="Status"
                                name="status"
                                key="status"
                              >
                                <Select
                                  size={'large'}
                                  allowClear={true}
                                  style={{ width: '100' }}
                                  onChange={() => {
                                    setIsFormReady(
                                      form.isFieldTouched('status') &&
                                        ((form.getFieldValue('status') ===
                                          'cancelled' &&
                                          form.getFieldValue(
                                            'cancellationReason',
                                          )) ||
                                          form.getFieldValue('status') !==
                                            'cancelled'),
                                    );
                                    setVisibleCancellationReason(
                                      form.getFieldValue('status') ===
                                        'cancelled',
                                    );
                                  }}
                                >
                                  <Option value="new" key="0">
                                    New
                                  </Option>
                                  <Option value="fullFilled" key="1">
                                    FullFilled
                                  </Option>
                                  <Option value="paid" key="2">
                                    Paid
                                  </Option>
                                  <Option value="cancelled" key="3">
                                    Cancelled
                                  </Option>
                                </Select>
                              </Form.Item>
                              {visibleCancellationReason && (
                                <Form.Item
                                  label="Cancellation Reason"
                                  name="cancellationReason"
                                  key="cancellationReason"
                                  onChange={() => {
                                    setIsFormReady(
                                      form.isFieldTouched('status') &&
                                        ((form.getFieldValue('status') ===
                                          'cancelled' &&
                                          form.getFieldValue(
                                            'cancellationReason',
                                          )) ||
                                          form.getFieldValue('status') !==
                                            'cancelled'),
                                    );
                                  }}
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        'Please input the cancellation reason!',
                                    },
                                  ]}
                                >
                                  <TextArea
                                    size="large"
                                    placeholder="Enter the cancellation reason"
                                  />
                                </Form.Item>
                              )}
                              <Form.Item
                                wrapperCol={{
                                  offset: 0,
                                  span: 24,
                                }}
                                style={{ textAlign: 'right' }}
                              >
                                <Button
                                  className="btn"
                                  type="primary"
                                  htmlType="submit"
                                  disabled={!isFormReady}
                                >
                                  Update Status
                                </Button>
                              </Form.Item>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    ) : (
                      'Loading ...'
                    )
                  ) : (
                    ''
                  )}
                </Col>
              </Row>
            </Spin>

            <Table
              columns={columns}
              dataSource={dataSource}
              title="product"
              link="/product"
              scroll={{ x: 1550 }}
              hidePagination={true}
              defSortKey="ProductId.Code:ASC,id:ASC"
            />
            <div className="btn-gp">
              {canEdit ? (
                <Link className="btn" to={`/orders/edit/${props.data?.id}`}>
                  Edit
                </Link>
              ) : (
                <Row>
                  <Col
                    span={24}
                    style={{
                      textAlign: 'center',
                      fontWeight: 500,
                      fontSize: '16px',
                      lineHeight: '120%',
                      color: '#4D5E55',
                    }}
                  >
                    {isFrozen
                      ? DELIVERY_FREEZE_ERROR
                      : 'Changes to orders are only allowed within 24 hours'}
                  </Col>
                </Row>
              )}
              {isAdmin && !isDeliveryDateFrozen(props.data?.DeliveryDate) && (
                <Link
                  className="btn"
                  to={`/orders/new?districtId=${props.data?.DistrictUserId?.id}&schoolId=${
                    props.data?.SchoolUserId?.id
                  }&deliveryDate=${props.data?.DeliveryDate}&shippingAddress=${encodeURIComponent(
                    props.data?.shippingAddress || '',
                  )}&shippingSiteName=${encodeURIComponent(
                    props.data?.shippingSiteName || '',
                  )}`}
                  style={{ marginLeft: '10px' }}
                >
                  Add Order
                </Link>
              )}
            </div>
            <div style={{ height: '120px' }}></div>
          </div>
        )}
        {!permit && (
          <div style={{ marginTop: '100px' }}>
            <Row>
              <Col
                span={24}
                style={{
                  textAlign: 'center',
                  margin: '16px 0',
                  fontWeight: 700,
                  fontSize: '56px',
                  lineHeight: '110%',
                  color: '#4D5E55',
                }}
              >
                OOPS!
              </Col>
            </Row>
            <Row>
              <Col
                span={24}
                style={{
                  textAlign: 'center',
                  fontWeight: 400,
                  fontSize: '32px',
                  lineHeight: '120%',
                  color: '#4D5E55',
                }}
              >
                Order not found
              </Col>
            </Row>
            <Row>
              <Col span={24} style={{ textAlign: 'center', margin: '70px 0' }}>
                <Link
                  to={'/orders'}
                  className="btn"
                  style={{ margin: 0, minWidth: '400px' }}
                >
                  {'Go to Orders '}
                </Link>
              </Col>
            </Row>
          </div>
        )}
        </div>
      </div>
      {permit && (
        <div className="print-only order-print-page">
          <h1>Placed Order</h1>
          <div className="print-order-summary">
            <div>
              <PrintInfo title="Order Number" text={props.data.id} />
              <PrintInfo
                title="District Name"
                text={formatText(
                  props.data.DistrictUserId?.DistrictOrSchoolName,
                )}
              />
              <PrintInfo
                title="Shipping Site Name"
                text={formatText(props.data.shippingSiteName)}
              />
              <PrintInfo
                title="Total Commercial Cost"
                text={`$${calculateUpdatedTotalCommercialCost()}`}
              />
              <PrintInfo
                title="Total Fulfilled Commercial"
                text={formatCurrency(fulfilledRegular)}
              />
            </div>
            <div>
              <PrintInfo
                title="School Name"
                text={formatText(props.data.SchoolUserId?.DistrictOrSchoolName)}
              />
              <PrintInfo
                title="Shipping Address"
                text={formatText(
                  props?.myData?.shippingAddress || props?.data?.shippingAddress,
                )}
              />
              <PrintInfo
                title="Total Commodity Price"
                text={`$${calculateUpdatedTotalCommodityCost()}`}
              />
              <PrintInfo
                title="Total Fulfilled Commodity"
                text={formatCurrency(fulfilledCommodity)}
              />
              <PrintInfo
                title="Delivery Date"
                text={formatDate(props.data.DeliveryDate)}
              />
            </div>
          </div>

          <div className="print-products">
            {dataSource.map((item) => {
              const product = item.ProductId || {};
              return (
                <div className="print-product-card" key={item.id}>
                  <div>
                    <PrintProductInfo
                      title="Code"
                      text={formatText(product.Code)}
                    />
                    <PrintProductInfo
                      title="Product name"
                      text={formatText(product.Name)}
                    />
                    <PrintProductInfo
                      title="Date Submitted"
                      text={formatDate(item.created_at)}
                    />
                    <PrintProductInfo
                      title="Quantity"
                      text={formatText(getCurrentQuantity(item))}
                    />
                    <PrintProductInfo
                      title="Time Lead"
                      text={
                        product.TimeLead ? `${product.TimeLead} days` : 'N/A'
                      }
                    />
                    <PrintProductInfo
                      title="Commercial Cost"
                      text={formatCurrency(product.ReqularPrice)}
                    />
                  </div>
                  <div>
                    <PrintProductInfo
                      title="Commodity Price"
                      text={formatCurrency(product.CommidityPrice)}
                    />
                    <PrintProductInfo
                      title="Fulfilled"
                      text={item.IsFulfilled ? 'Yes' : 'No'}
                    />
                    <PrintProductInfo
                      title="Fulfilled Quantity"
                      text={
                        item.IsFulfilled
                          ? formatText(item.FulfilledQuantity || 0)
                          : 'N/A'
                      }
                    />
                    <PrintProductInfo
                      title="Commercial Total"
                      text={getCommercialTotal(item)}
                    />
                    <PrintProductInfo
                      title="Commodity Total"
                      text={getCommodityTotal(item)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function mapStateToProps(state) {
  return {
    data: state.order.data,
    loading: state.order.isLoading,

    tableData: state.data.data,
    tableLoading: state.data.isLoading,
    myData: state.me.data,
    roleId: state.me.roleId,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setUrl: (q) => dispatch(setUrl(q)),
    setSelect: (q) => dispatch(setSelect(q)),
    setQuery: (q) => dispatch(setQuery(q)),
    setSelectedMenu: (id) => dispatch(setSelectedMenu(id)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderInfoPage);
